import { inject, Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  PushNotificationSchema,
} from '@capacitor/push-notifications';
import { environment } from '../../../../environments/environment';
import { urlMap } from '../../../../firebase-messaging/message-handler';
import { BaseNotificationData } from '@fc-core/services/notification-payload';
import { PushService } from './push.service';

@Injectable()
export class NativePushService extends PushService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private zone = inject(NgZone);
  private listenersAttached = false;

  async init(): Promise<void> {
    const granted = await this.ensurePermission();
    if (!granted) {
      return;
    }
    this.attachListeners();
    await PushNotifications.register();
  }

  private async ensurePermission(): Promise<boolean> {
    let status = await PushNotifications.checkPermissions();
    if (
      status.receive === 'prompt' ||
      status.receive === 'prompt-with-rationale'
    ) {
      status = await PushNotifications.requestPermissions();
    }
    return status.receive === 'granted';
  }

  private attachListeners(): void {
    if (this.listenersAttached) {
      return;
    }
    this.listenersAttached = true;

    PushNotifications.addListener('registration', (token) =>
      this.sendTokenToBackend(token.value),
    );
    PushNotifications.addListener('registrationError', (error) =>
      console.error('Push registration failed:', error),
    );
    PushNotifications.addListener('pushNotificationActionPerformed', (action) =>
      this.openNotificationTarget(action.notification),
    );
  }

  // Payload mirrors the legacy Flutter wrapper (fc-console) so the backend
  // treats this app as the same push client.
  private sendTokenToBackend(token: string): void {
    const platform = Capacitor.getPlatform();
    this.http
      .post(environment.apiUrl + 'api/push/token', {
        token,
        platform,
        name: `console-wrapper-${platform}`,
        app: 'console',
      })
      .subscribe({
        // Best-effort: register() runs on every launch and re-sends the token.
        error: () => {},
      });
  }

  private openNotificationTarget(notification: PushNotificationSchema): void {
    const data = (notification.data ?? {}) as BaseNotificationData;
    const buildUrl = urlMap[data.link_type as keyof typeof urlMap];
    if (!buildUrl || !data.link_id) {
      return;
    }
    this.zone.run(() => this.router.navigateByUrl(buildUrl(data.link_id)));
  }
}
